import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { appRepo } from "../../repo/app-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { PaymentData, PaymentPayType, PaymentStats, PaymentStatus } from "../../entry/server/app.ts";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";

const PAGE_LIMITS = [10, 20] as const;

const emptyStats: PaymentStats = {
  paid_count: 0,
  failed_count: 0,
  pending_count: 0,
  total_sales: 0,
};

function formatToman(amount: number) {
  const value = Number.isFinite(amount) ? Math.round(amount) : 0;
  return `${value.toLocaleString("fa-IR")} تومان`;
}

function formatCount(value: number) {
  const n = Number.isFinite(value) ? value : 0;
  return n.toLocaleString("fa-IR");
}

function getStatusMeta(status: string) {
  const normalized = (status || "").toLowerCase() as PaymentStatus;

  if (normalized === "pending") {
    return { label: "در انتظار", className: "bg-sky-500/20 text-sky-300" };
  }
  if (normalized === "paid") {
    return { label: "پرداخت‌شده", className: "bg-emerald-500/20 text-emerald-300" };
  }
  if (normalized === "failed") {
    return { label: "ناموفق", className: "bg-rose-500/20 text-rose-300" };
  }

  return { label: status || "—", className: "bg-slate-700/60 text-slate-300" };
}

function getPayTypeMeta(payType: string) {
  const normalized = (payType || "").toLowerCase() as PaymentPayType;

  if (normalized === "irr") {
    return { label: "تومان", className: "bg-slate-700/60 text-slate-200" };
  }
  if (normalized === "usdt") {
    return { label: "تتر", className: "bg-amber-500/20 text-amber-300" };
  }
  if (normalized === "wallet") {
    return { label: "کیف پول", className: "bg-violet-500/20 text-violet-300" };
  }

  return { label: payType || "—", className: "bg-slate-700/60 text-slate-300" };
}

export default function TransactionsScreen() {
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<(typeof PAGE_LIMITS)[number]>(10);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const { setMessage } = useContext(AppContext);

  const fetchPayments = async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    try {
      const response = await appRepo.getPayments(page, limit);
      if (!response.status || !response.data) {
        setMessage({ type: "error", message: response.message || "دریافت لیست تراکنش‌ها ناموفق بود." });
        if (!options?.silent) {
          setPayments([]);
        }
        return;
      }
      setPayments(response.data);
      setPagination(response.pagination ?? null);
      if (response.stats) {
        setStats(response.stats);
      }
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      if (!options?.silent) {
        setPayments([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  const patchStatsAfterApprove = (amount: number) => {
    setStats((prev) => {
      const current = prev ?? emptyStats;
      return {
        paid_count: current.paid_count + 1,
        pending_count: Math.max(0, current.pending_count - 1),
        failed_count: current.failed_count,
        total_sales: current.total_sales + Math.round(amount || 0),
      };
    });
  };

  const patchStatsAfterReject = () => {
    setStats((prev) => {
      const current = prev ?? emptyStats;
      return {
        ...current,
        pending_count: Math.max(0, current.pending_count - 1),
        failed_count: current.failed_count + 1,
      };
    });
  };

  const onApprove = async (payment: PaymentData) => {
    if (actionLoading !== null) return;
    if (!window.confirm("این پرداخت تأیید شود؟ اشتراک VIP کاربر تمدید می‌شود.")) return;

    setActionLoading(payment.id);
    try {
      const res = await appRepo.approvePayment(payment.id);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "تأیید پرداخت ناموفق بود." });
        return;
      }
      setPayments((prev) => prev.map((item) => (item.id === payment.id ? { ...item, status: "paid" } : item)));
      if (payment.status === "pending") {
        patchStatsAfterApprove(payment.amount);
      }
      setMessage({ type: "success", message: res.message || "پرداخت با موفقیت تایید شد." });
      await fetchPayments({ silent: true });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setActionLoading(null);
    }
  };

  const onReject = async (payment: PaymentData) => {
    if (actionLoading !== null) return;
    if (!window.confirm("این تراکنش رد شود؟")) return;

    setActionLoading(payment.id);
    try {
      const res = await appRepo.rejectPayment(payment.id);
      if (!res.status) {
        setMessage({ type: "error", message: res.message || "رد تراکنش ناموفق بود." });
        return;
      }

      const updated = res.data;
      if (updated) {
        setPayments((prev) => prev.map((item) => (item.id === payment.id ? updated : item)));
      }

      if (updated?.status === "failed" && payment.status === "pending") {
        patchStatsAfterReject();
        setMessage({ type: "success", message: "تراکنش با موفقیت رد شد." });
      } else if (updated && updated.status === payment.status) {
        setMessage({
          type: "info",
          message: "سرور وضعیت تراکنش را تغییر نداد. این عملیات فعلاً فقط جزئیات پرداخت را برمی‌گرداند.",
        });
      } else {
        setMessage({ type: "success", message: res.message || "عملیات رد تراکنش انجام شد." });
      }

      await fetchPayments({ silent: true });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setActionLoading(null);
    }
  };

  const onCopyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setMessage({ type: "success", message: "توکن درگاه کپی شد." });
    } catch {
      setMessage({ type: "error", message: "کپی توکن ناموفق بود." });
    }
  };

  const onLimitChange = (nextLimit: (typeof PAGE_LIMITS)[number]) => {
    setLimit(nextLimit);
    setPage(1);
  };

  const visibleStats = stats ?? emptyStats;

  return (
    <section className="mt-4 space-y-4">
      <AdminPageTitle title="تراکنش‌ها" />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <article className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
          <p className="text-xs text-emerald-200/80">پرداخت‌شده</p>
          <p className="mt-2 text-2xl font-semibold text-emerald-200">{formatCount(visibleStats.paid_count)}</p>
        </article>
        <article className="rounded-2xl border border-sky-500/20 bg-sky-500/10 p-4">
          <p className="text-xs text-sky-200/80">در انتظار</p>
          <p className="mt-2 text-2xl font-semibold text-sky-200">{formatCount(visibleStats.pending_count)}</p>
        </article>
        <article className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-4">
          <p className="text-xs text-rose-200/80">ناموفق</p>
          <p className="mt-2 text-2xl font-semibold text-rose-200">{formatCount(visibleStats.failed_count)}</p>
        </article>
        <article className="rounded-2xl border border-indigo-500/20 bg-indigo-500/10 p-4">
          <p className="text-xs text-indigo-200/80">مجموع فروش</p>
          <p className="mt-2 text-xl font-semibold text-indigo-100">{formatToman(visibleStats.total_sales)}</p>
        </article>
      </div>

      <p className="text-xs text-slate-500">
        مبالغ به تومان هستند. تراکنش‌های تتر با نرخ تبدیل تنظیمات به تومان محاسبه شده‌اند. آمار فقط در صفحه اول از سرور می‌آید.
      </p>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
        <p className="mb-2 text-xs text-slate-400">تعداد در هر صفحه</p>
        <div className="flex flex-wrap gap-2">
          {PAGE_LIMITS.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => onLimitChange(item)}
              className={`rounded-full px-3 py-1.5 text-xs transition ${
                limit === item
                  ? "bg-indigo-500 text-white"
                  : "border border-slate-700 text-slate-300 hover:border-indigo-400 hover:text-indigo-300"
              }`}
            >
              {item.toLocaleString("fa-IR")} مورد
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت لیست تراکنش‌ها...
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60">
            <table className="min-w-full divide-y divide-slate-800 text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">شناسه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">کاربر</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">پلن</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">درگاه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">نوع پرداخت</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">مبلغ</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">کد تخفیف</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">توکن درگاه</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">وضعیت</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">تاریخ</th>
                  <th className="px-4 py-3 text-right font-medium text-slate-300">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {payments.map((payment) => {
                  const statusMeta = getStatusMeta(payment.status);
                  const payTypeMeta = getPayTypeMeta(payment.pay_type);
                  const isPending = payment.status === "pending";
                  const busy = actionLoading === payment.id;

                  return (
                    <tr key={payment.id} className="transition hover:bg-slate-900">
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">{payment.id}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {payment.user_id ? (
                          <Link to={`/admin/users/${payment.user_id}`} className="text-blue-500 transition hover:text-blue-400">
                            {payment.username || `کاربر #${payment.user_id}`}
                          </Link>
                        ) : (
                          <span className="text-slate-400">{payment.username || "—"}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {payment.plan_id ? (
                          <Link to={`/admin/plans/${payment.plan_id}/edit`} className="text-blue-500 transition hover:text-blue-400">
                            {payment.plan_name || `پلن #${payment.plan_id}`}
                          </Link>
                        ) : (
                          <span className="text-slate-400">{payment.plan_name || "—"}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {payment.gateway_id ? (
                          <Link to={`/admin/gateways/${payment.gateway_id}/edit`} className="text-blue-500 transition hover:text-blue-400">
                            {payment.gateway_name || `درگاه #${payment.gateway_id}`}
                          </Link>
                        ) : (
                          <span className="text-slate-400">{payment.gateway_name || "—"}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${payTypeMeta.className}`}>
                          {payTypeMeta.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-200">{formatToman(payment.amount)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">{payment.discount_code || "—"}</td>
                      <td className="px-4 py-3">
                        {payment.gateway_token ? (
                          <div className="flex max-w-[12rem] items-center gap-2">
                            <span className="truncate font-mono text-xs text-slate-400" title={payment.gateway_token} dir="ltr">
                              {payment.gateway_token}
                            </span>
                            <button
                              type="button"
                              onClick={() => void onCopyToken(payment.gateway_token)}
                              className="shrink-0 rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-300 transition hover:border-indigo-400 hover:text-indigo-300"
                            >
                              کپی
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-300">{payment.created_at || "—"}</td>
                      <td className="px-4 py-3">
                        {isPending ? (
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void onApprove(payment)}
                              className="rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500 hover:text-indigo-300 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {busy ? "در حال ثبت…" : "تأیید"}
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => void onReject(payment)}
                              className="rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:border-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              رد
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {!payments.length && (
                  <tr>
                    <td colSpan={11} className="px-4 py-6 text-center text-sm text-slate-400">
                      تراکنشی یافت نشد.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pagination ? <PaginationView pagination={pagination} onPageChange={setPage} /> : null}
        </>
      )}
    </section>
  );
}
